import { create } from 'zustand';
import { projectsService } from '../services/projectsService';
import type { Game, GameVersion, GameFilters } from '../types/api';
import { analyticsService } from '../services/analyticsService';

interface ProjectsState {
  // Data
  projects: Game[];
  recentProjects: Game[];
  currentProject: Game | null;
  currentProjectVersions: GameVersion[];
  dashboardStats: {
    total_projects: number;
    total_versions: number;
    active_projects: number;
    archived_projects: number;
  } | null;

  // Loading states
  isLoadingProjects: boolean;
  isLoadingRecentProjects: boolean;
  isLoadingCurrentProject: boolean;
  isLoadingVersions: boolean;
  isLoadingStats: boolean;
  
  // Prevent duplicate fetches and flickering
  hasLoadedProjects: boolean;
  isInitialized: boolean;
  lastFetchTime: number;

  // Error states
  projectsError: string | null;
  recentProjectsError: string | null;
  currentProjectError: string | null;
  versionsError: string | null;
  statsError: string | null;

  // Actions
  refreshProjects: () => Promise<void>;
  fetchProjects: (filters?: GameFilters) => Promise<void>;
  fetchRecentProjects: (limit?: number) => Promise<void>;
  fetchProjectDetails: (projectId: string) => Promise<void>;
  fetchProjectVersions: (projectId: string) => Promise<void>;
  fetchDashboardStats: () => Promise<void>;
  searchProjects: (query: string, filters?: GameFilters) => Promise<void>;
  updateProject: (projectId: string, data: any) => Promise<void>;
  togglePublishStatus: (projectId: string, unpublish: boolean) => Promise<void>;
  toggleVisibility: (projectId: string, makePrivate: boolean) => Promise<void>;
  deleteProject: (projectId: string) => Promise<void>;
  duplicateProject: (projectId: string, newName?: string) => Promise<Game>;
  exportProject: (projectId: string) => Promise<void>;
  rollbackToVersion: (projectId: string, versionId: string) => Promise<void>;
  activateVersion: (projectId: string, versionId: string) => Promise<void>;
  archiveVersion: (projectId: string, versionId: string) => Promise<void>;
  deleteVersion: (projectId: string, versionId: string) => Promise<void>;
  downloadVersion: (projectId: string, versionId: string, downloadUrl?: string) => Promise<void>;
  editVersion: (projectId: string, versionId: string) => Promise<void>;
  clearCurrentProject: () => void;
  clearErrors: () => void;
}

export const useProjectsStore = create<ProjectsState>((set, get) => ({
  // Initial state
  projects: [],
  recentProjects: [],
  currentProject: null,
  currentProjectVersions: [],
  dashboardStats: null,
  
  isLoadingProjects: false,
  isLoadingRecentProjects: false,
  isLoadingCurrentProject: false,
  isLoadingVersions: false,
  isLoadingStats: false,
  hasLoadedProjects: false,
  isInitialized: false,
  lastFetchTime: 0,
  
  projectsError: null,
  recentProjectsError: null,
  currentProjectError: null,
  versionsError: null,
  statsError: null,

  // Actions
  refreshProjects: async () => {
    set({ hasLoadedProjects: false, isInitialized: false });
    await get().fetchProjects();
  },

  fetchProjects: async (filters?: GameFilters) => {
    const { isLoadingProjects, hasLoadedProjects, isInitialized, lastFetchTime } = get();
    const now = Date.now();
    
    // Prevent duplicate fetches
    if (isLoadingProjects) {
      console.log('🔄 Already loading projects, skipping duplicate fetch');
      return;
    }
    
    // Skip if already loaded and no filters, and we're initialized
    if (hasLoadedProjects && !filters && isInitialized) {
      console.log('🔄 Projects already loaded and initialized, skipping duplicate fetch');
      return;
    }
    
    // Prevent rapid successive fetches (within 1 second)
    if (now - lastFetchTime < 1000 && lastFetchTime > 0) {
      console.log('🔄 Recent fetch detected, skipping duplicate fetch');
      return;
    }
    
    console.log('🏪 Fetching projects from store with filters:', filters);
    
    set({ isLoadingProjects: true, projectsError: null });
    
    try {
      const projects = await projectsService.getUserProjects(filters);
      console.log('✅ Projects fetched successfully:', projects.length);
      
      set({ 
        projects, 
        isLoadingProjects: false,
        projectsError: null,
        hasLoadedProjects: true,
        isInitialized: true,
        lastFetchTime: Date.now()
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch projects';
      console.error('❌ Store: Failed to fetch projects:', errorMessage);
      
      set({ 
        isLoadingProjects: false, 
        projectsError: errorMessage,
        projects: [],
        isInitialized: true,
        lastFetchTime: Date.now()
      });
    }
  },

  fetchRecentProjects: async (limit = 5) => {
    console.log('🏪 Fetching recent projects from store, limit:', limit);
    
    set({ isLoadingRecentProjects: true, recentProjectsError: null });
    
    try {
      const recentProjects = await projectsService.getRecentProjects(limit);
      console.log('✅ Recent projects fetched successfully:', recentProjects.length);
      
      set({ 
        recentProjects, 
        isLoadingRecentProjects: false,
        recentProjectsError: null 
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch recent projects';
      console.error('❌ Store: Failed to fetch recent projects:', errorMessage);
      
      set({ 
        isLoadingRecentProjects: false, 
        recentProjectsError: errorMessage,
        recentProjects: [] 
      });
    }
  },

  fetchProjectDetails: async (projectId: string) => {
    console.log('🏪 Fetching project details from store:', projectId);
    
    set({ isLoadingCurrentProject: true, currentProjectError: null });
    
    try {
      const project = await projectsService.getProjectDetails(projectId);
      console.log('✅ Project details fetched successfully:', project.name);
      
      set({ 
        currentProject: project, 
        isLoadingCurrentProject: false,
        currentProjectError: null 
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch project details';
      console.error('❌ Store: Failed to fetch project details:', errorMessage);
      
      set({ 
        isLoadingCurrentProject: false, 
        currentProjectError: errorMessage,
        currentProject: null 
      });
    }
  },

  fetchProjectVersions: async (projectId: string) => {
    console.log('🏪 Fetching project versions from store:', projectId);
    
    set({ isLoadingVersions: true, versionsError: null });
    
    try {
      const versions = await projectsService.getProjectVersions(projectId);
      console.log('✅ Project versions fetched successfully:', versions.length);
      
      set({ 
        currentProjectVersions: versions, 
        isLoadingVersions: false,
        versionsError: null 
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch project versions';
      console.error('❌ Store: Failed to fetch project versions:', errorMessage);
      
      set({ 
        isLoadingVersions: false, 
        versionsError: errorMessage,
        currentProjectVersions: [] 
      });
    }
  },

  fetchDashboardStats: async () => {
    console.log('🏪 Fetching dashboard stats from store');
    
    set({ isLoadingStats: true, statsError: null });
    
    try {
      const stats = await projectsService.getDashboardStats();
      console.log('✅ Dashboard stats fetched successfully:', stats);
      
      set({ 
        dashboardStats: stats, 
        isLoadingStats: false,
        statsError: null 
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch dashboard stats';
      console.error('❌ Store: Failed to fetch dashboard stats:', errorMessage);
      
      set({ 
        isLoadingStats: false, 
        statsError: errorMessage,
        dashboardStats: null 
      });
    }
  },

  searchProjects: async (query: string, filters?: GameFilters) => {
    console.log('🏪 Searching projects from store:', query);
    
    set({ isLoadingProjects: true, projectsError: null, hasLoadedProjects: false });
    
    try {
      const projects = await projectsService.searchProjects(query, filters);
      console.log('✅ Project search completed:', projects.length, 'results');
      
      // Track search
      analyticsService.track({
        name: 'projects_searched',
        properties: {
          query,
          results_count: projects.length,
          has_filters: !!filters
        }
      });
      
      set({ 
        projects, 
        isLoadingProjects: false,
        projectsError: null,
        hasLoadedProjects: true
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to search projects';
      console.error('❌ Store: Failed to search projects:', errorMessage);
      
      set({ 
        isLoadingProjects: false, 
        projectsError: errorMessage,
        projects: [] 
      });
    }
  },

  updateProject: async (projectId: string, data: any) => {
    console.log('🏪 Updating project from store:', projectId, data);
    
    try {
      const updatedProject = await projectsService.updateProject(projectId, data);
      console.log('✅ Project updated successfully:', updatedProject.name);
      
      // Update the project in the current state
      const { projects, currentProject } = get();
      
      set({
        projects: projects.map(p => 
          p.id === projectId ? { ...p, ...updatedProject } : p
        ),
        currentProject: currentProject && currentProject.id === projectId 
          ? { ...currentProject, ...updatedProject } 
          : currentProject,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update project';
      console.error('❌ Store: Failed to update project:', errorMessage);
      throw error;
    }
  },

  togglePublishStatus: async (projectId: string, unpublish: boolean) => {
    console.log('🏪 Toggling project publish status from store:', projectId, 'unpublish:', unpublish);
    
    try {
      await projectsService.togglePublishStatus(projectId, unpublish);
      console.log('✅ Project publish status toggled successfully');
      
      // Determine the new status based on unpublish state
      const newStatus: 'active' | 'inactive' = unpublish ? 'inactive' : 'active';
      
      // Update the project in the projects array immediately
      const { projects, currentProject } = get();
      const updatedProjects = projects.map(p => 
        p.id === projectId ? { ...p, status: newStatus } : p
      );
      
      // Update the current project if it matches
      const updatedCurrentProject = currentProject && currentProject.id === projectId
        ? { ...currentProject, status: newStatus }
        : currentProject;
      
      // Update the store state immediately
      set({
        projects: updatedProjects,
        currentProject: updatedCurrentProject,
      });
      
      // Refresh project details and versions to get any other updated fields
      const { fetchProjectDetails, fetchProjectVersions } = get();
      await fetchProjectDetails(projectId);
      await fetchProjectVersions(projectId);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to toggle project publish status';
      console.error('❌ Store: Failed to toggle project publish status:', errorMessage);
      throw error;
    }
  },

  toggleVisibility: async (projectId: string, makePrivate: boolean) => {
    console.log('🏪 Toggling visibility from store:', projectId, 'makePrivate:', makePrivate);
    
    try {
      // Determine the new visibility
      const newVisibility: 'public' | 'private' = makePrivate ? 'private' : 'public';
      
      // Update the project in the projects array immediately for instant UI feedback
      const { projects, currentProject } = get();
      const updatedProjects = projects.map(p => 
        p.id === projectId && p.content
          ? { ...p, content: { ...p.content, visibility: newVisibility } }
          : p
      );
      
      // Update the current project if it matches
      const updatedCurrentProject = currentProject && currentProject.id === projectId && currentProject.content
        ? { ...currentProject, content: { ...currentProject.content, visibility: newVisibility } }
        : currentProject;
      
      // Update the store state immediately
      set({
        projects: updatedProjects,
        currentProject: updatedCurrentProject,
      });
      
      // Call the API to persist the change
      await projectsService.toggleVisibility(projectId, makePrivate);
      console.log('✅ Visibility toggled successfully');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to toggle visibility';
      console.error('❌ Store: Failed to toggle visibility:', errorMessage);
      throw error;
    }
  },

  deleteProject: async (projectId: string) => {
    console.log('🏪 Deleting project from store:', projectId);
    
    try {
      await projectsService.deleteProject(projectId);
      console.log('✅ Project deleted successfully');
      
      // Remove the project from current state
      const { projects } = get();
      
      set({
        projects: projects.filter(p => p.id !== projectId),
        currentProject: null,
        currentProjectVersions: [],
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete project';
      console.error('❌ Store: Failed to delete project:', errorMessage);
      throw error;
    }
  },

  duplicateProject: async (projectId: string, newName?: string) => {
    console.log('🏪 Duplicating project from store:', projectId, 'with name:', newName);
    
    try {
      const duplicatedProject = await projectsService.duplicateProject(projectId, newName);
      console.log('✅ Project duplicated successfully:', duplicatedProject.name);
      
      // Add the new project to the current state
      const { projects } = get();
      
      set({
        projects: [...projects, duplicatedProject],
      });
      
      return duplicatedProject;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to duplicate project';
      console.error('❌ Store: Failed to duplicate project:', errorMessage);
      throw error;
    }
  },

  exportProject: async (projectId: string) => {
    console.log('🏪 Exporting project from store:', projectId);
    
    try {
      await projectsService.exportProject(projectId);
      console.log('✅ Project export initiated successfully');
      
      // No need to update state for exports
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to export project';
      console.error('❌ Store: Failed to export project:', errorMessage);
      throw error;
    }
  },

  rollbackToVersion: async (projectId: string, versionId: string) => {
    console.log('🏪 Rolling back project from store:', projectId, 'to version:', versionId);
    
    try {
      await projectsService.rollbackToVersion(projectId, versionId);
      console.log('✅ Rollback completed successfully');
      
      // Refresh project details and versions
      const { fetchProjectDetails, fetchProjectVersions } = get();
      await fetchProjectDetails(projectId);
      await fetchProjectVersions(projectId);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to rollback project';
      console.error('❌ Store: Failed to rollback project:', errorMessage);
      throw error;
    }
  },

  activateVersion: async (projectId: string, versionId: string) => {
    console.log('🏪 Activating version from store:', projectId, 'version:', versionId);
    
    try {
      // Use rollbackToVersion as activating is essentially the same as rolling back to a version
      await projectsService.rollbackToVersion(projectId, versionId);
      console.log('✅ Version activated successfully');
      
      // Refresh project details and versions
      const { fetchProjectDetails, fetchProjectVersions } = get();
      await fetchProjectDetails(projectId);
      await fetchProjectVersions(projectId);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to activate version';
      console.error('❌ Store: Failed to activate version:', errorMessage);
      throw error;
    }
  },

  archiveVersion: async (projectId: string, versionId: string) => {
    console.log('🏪 Archiving version from store:', projectId, 'version:', versionId);
    
    try {
      await projectsService.archiveVersion(projectId, versionId);
      console.log('✅ Version archived successfully');
      
      // Refresh project versions to update the UI
      const { fetchProjectVersions } = get();
      await fetchProjectVersions(projectId);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to archive version';
      console.error('❌ Store: Failed to archive version:', errorMessage);
      throw error;
    }
  },

  deleteVersion: async (projectId: string, versionId: string) => {
    console.log('🏪 Deleting version from store:', projectId, 'version:', versionId);
    
    try {
      await projectsService.deleteVersion(projectId, versionId);
      console.log('✅ Version deleted successfully');
      
      // Remove the version from current state and refresh
      const { currentProjectVersions, fetchProjectVersions } = get();
      const updatedVersions = currentProjectVersions.filter(v => v.id !== versionId);
      
      set({
        currentProjectVersions: updatedVersions,
      });
      
      // Refresh project details and versions to get updated counts
      const { fetchProjectDetails } = get();
      await fetchProjectDetails(projectId);
      await fetchProjectVersions(projectId);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete version';
      console.error('❌ Store: Failed to delete version:', errorMessage);
      throw error;
    }
  },

  downloadVersion: async (projectId: string, versionId: string, downloadUrl?: string) => {
    console.log('🏪 Downloading version from store:', projectId, 'version:', versionId);
    
    try {
      if (downloadUrl) {
        // Direct download using the URL
        window.open(downloadUrl, '_blank');
      } else {
        // Use the service method for download
        await projectsService.downloadVersion(projectId, versionId);
      }
      console.log('✅ Version download initiated');
      
      // No need to refresh state for downloads
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to download version';
      console.error('❌ Store: Failed to download version:', errorMessage);
      throw error;
    }
  },

  editVersion: async (projectId: string, versionId: string) => {
    console.log('🏪 Editing version from store:', projectId, 'version:', versionId);
    
    try {
      // For now, just log the edit action
      // In a real implementation, this would open an edit modal or redirect to edit page
      console.log('✅ Version edit initiated');
      
      // No need to refresh state for starting an edit
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to edit version';
      console.error('❌ Store: Failed to edit version:', errorMessage);
      throw error;
    }
  },

  clearCurrentProject: () => {
    console.log('🏪 Clearing current project from store');
    set({
      currentProject: null,
      currentProjectVersions: [],
      currentProjectError: null,
      versionsError: null,
    });
  },

  clearErrors: () => {
    console.log('🏪 Clearing all errors from store');
    set({
      projectsError: null,
      recentProjectsError: null,
      currentProjectError: null,
      versionsError: null,
      statsError: null,
    });
  },
})); 